import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

interface HomeScreenProps {
  isAuthenticated: boolean;
}

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
`;

const Title = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: 4rem;
  font-weight: 900;
  margin-bottom: 20px;
  color: var(--color-black);
  letter-spacing: 2px;
  
  @media (max-width: 768px) {
    font-size: 3rem;
    letter-spacing: 1px;
  }
`;

const MainTitle = styled.h1`
  font-family: "Playwrite DE SAS", cursive;
  font-size: 3.5rem;
  font-weight: 700;
  margin: 0 auto 20px auto;
  color: var(--color-black);
  letter-spacing: 0px;
  white-space: nowrap;
  text-align: center;
  
  @media (max-width: 1024px) {
    font-size: 3rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    white-space: normal;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.h2`
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  font-size: 1.6rem;
  font-weight: 400;
  margin-bottom: 60px;
  color: #666666;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
`;

const Button = styled.button`
  background-color: var(--color-black) !important;
  color: var(--color-white) !important;
  padding: 16px 32px !important;
  font-size: 1.1rem !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  border: 2px solid var(--color-black) !important;
  display: inline-block !important;
  box-sizing: border-box !important;
  min-width: 200px !important;
  width: auto !important;
  text-align: center !important;

  &:hover {
    background-color: var(--color-white) !important;
    color: var(--color-black) !important;
  }
  
  &:active {
    transform: translateY(0) !important;
  }
`;

const LoginModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LoginModalContent = styled.div`
  background: var(--color-white);
  border: 2px solid var(--color-light-gray);
  padding: 40px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const LoginModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--color-black);
  margin-bottom: 15px;
`;

const LoginModalText = styled.p`
  font-size: 1rem;
  color: var(--color-gray);
  margin-bottom: 25px;
`;

const LoginModalButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const LoginModalButton = styled.button<{ $primary?: boolean }>`
  background-color: ${props => props.$primary ? 'var(--color-black)' : 'var(--color-white)'};
  color: ${props => props.$primary ? 'var(--color-white)' : 'var(--color-black)'};
  border: 2px solid var(--color-black);
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$primary ? 'var(--color-white)' : 'var(--color-black)'};
    color: ${props => props.$primary ? 'var(--color-black)' : 'var(--color-white)'};
  }
`;

const FullBleedSection = styled.section`
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  background: #000;
`;

const VideoContainer = styled.div`
  position: relative;
`;

const PromoVideo = styled.video`
  display: block;
  width: 100%;
  height: auto;
  max-height: 80vh;
  object-fit: cover;
`;

const MuteToggleButton = styled.button`
  position: absolute;
  right: 16px;
  bottom: 16px;
  background: rgba(0, 0, 0, 0.6);
  color: var(--color-white);
  border: 1px solid var(--color-white);
  border-radius: 0;
  padding: 8px 12px;
  font-size: 0.9rem;
  cursor: pointer;
`;

const PlayOverlayButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.6);
  color: var(--color-white);
  border: 1px solid var(--color-white);
  border-radius: 0;
  padding: 12px 20px;
  font-size: 1rem;
  cursor: pointer;
`;

const HomeScreen: React.FC<HomeScreenProps> = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const handleGenerateWallpaper = () => {
    if (!isAuthenticated) {
      setTimeout(() => {
        setShowLoginModal(true);
      }, 0);
      return;
    }
    navigate('/wallpaper');
  };

  return (
    <>
      <Container>
        <MainTitle><span style={{ color: 'var(--color-blue)' }}>Unify</span> Your Style, Inside and Out.</MainTitle>
        <Subtitle>Instantly generate AI wallpapers that perfectly match your phone case.</Subtitle>
        
        <ButtonContainer>
          <Button onClick={handleGenerateWallpaper}>
            Generate Wallpaper
          </Button>
        </ButtonContainer>

        {/* Login Modal */}
        {showLoginModal && (
          <LoginModalOverlay onClick={() => setShowLoginModal(false)}>
            <LoginModalContent onClick={(e) => e.stopPropagation()}>
              <LoginModalTitle>Login Required</LoginModalTitle>
              <LoginModalText>
                Please log in to generate wallpapers. You'll get 3 free daily generations to start!
              </LoginModalText>
              <LoginModalButtonContainer>
                <LoginModalButton $primary onClick={() => {
                  setShowLoginModal(false);
                  navigate('/login');
                }}>
                  Go to Login
                </LoginModalButton>
                <LoginModalButton onClick={() => setShowLoginModal(false)}>
                  Cancel
                </LoginModalButton>
              </LoginModalButtonContainer>
            </LoginModalContent>
          </LoginModalOverlay>
        )}
      </Container>

      <FullBleedSection>
        <VideoContainer>
          <PromoVideo
            ref={videoRef}
            src="/accord-homepage-video.mov"
            playsInline
            muted={isMuted}
            controls={false}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
          />
          {!isPlaying && (
            <PlayOverlayButton
              aria-label="Play video"
              onClick={() => {
                if (!videoRef.current) return;
                videoRef.current.muted = isMuted;
                videoRef.current.play().catch(() => {});
              }}
            >
              Play
            </PlayOverlayButton>
          )}
          <MuteToggleButton
            aria-label={isMuted ? 'Unmute video' : 'Mute video'}
            onClick={() => {
              const next = !isMuted;
              setIsMuted(next);
              if (videoRef.current) {
                videoRef.current.muted = next;
              }
            }}
          >
            {isMuted ? 'Unmute' : 'Mute'}
          </MuteToggleButton>
        </VideoContainer>
      </FullBleedSection>
    </>
  );
};

export default HomeScreen;
